/**
 * @module NotificationTypeBadge
 * @package @geenius/notifications-react
 * @description Renders a small semantic badge for a notification type in the
 * Tailwind React variant.
 */

import { motion } from '@geenius/motion/react'
import type { NotificationType } from '@geenius/notifications/shared'
import { getNotificationTypeLabel, TYPE_CONFIG } from '@geenius/notifications/shared'
import type { JSX } from 'react'
import { useNotificationCopy } from '../lib/notificationCopy'
import {
  getNotificationAccentStyle,
  notificationTypeBadgeVariants,
} from '../lib/notificationStyles'
import { cn } from '../lib/utils'

/**
 * Props for the notification type badge.
 */
export interface NotificationTypeBadgeProps {
  type: NotificationType
  className?: string
}

/**
 * Renders a semantic badge for a notification type.
 *
 * @param props Badge props.
 * @returns The rendered badge.
 */
export function NotificationTypeBadge({
  type,
  className,
}: NotificationTypeBadgeProps): JSX.Element {
  const { t } = useNotificationCopy()
  const config = TYPE_CONFIG[type]

  return (
    <motion.span
      style={getNotificationAccentStyle(type)}
      whileHover={{ y: -1, scale: 1.02 }}
      className={cn(notificationTypeBadgeVariants(), className)}
    >
      {config.icon} {getNotificationTypeLabel(type, t)}
    </motion.span>
  )
}
